/* eslint no-console: 0 */
/* global axios */
// =============================================================================
// Fangate API client
//
// Wraps https://fangate.info/api (production) — Laravel Sanctum bearer auth.
//
// Token storage model (interim, until Hetzner backend lands):
//   - One token per Chatwoot inbox (Chat-Account), keyed by inbox id.
//   - localStorage key:  `fangate_token_<inboxId>`
//   - The token is set/cleared in Settings → Chat-Accounts → Edit
//     (FangateSettings.vue → edit-inbox modal → "Fangate vault" section).
//   - FangatePanel.vue calls `setActiveInbox(id)` whenever the active
//     conversation/inbox changes, switching which key getToken() reads.
//
// Where this lives in the stack:
//   - Today: thin client. Per-inbox tokens in browser localStorage.
//   - Later (Hetzner backend): tokens move server-side (encrypted, per
//     Chatwoot account + inbox); this client will hit our own
//     /api/fangate/* proxy endpoints. Only baseUrl() + lookup change —
//     the method signatures stay the same.
// =============================================================================

// Same-origin proxy — Rails route /integrations/fangate_proxy/* forwards
// to https://fangate.info/api/* (bypasses CORS, hides token in transit
// from the browser network tab).
//
// Direct mode (set window.FANGATE_DIRECT = true in console) bypasses the
// proxy for testing — useful only when CORS is allowed or in a CORS-disabled
// browser. Default: proxy.
const FANGATE_BASE_URL = '/integrations/fangate_proxy';
const FANGATE_DIRECT_URL = 'https://fangate.info/api';

// Vault state is scoped per Chatwoot inbox (Chat-Account). The TOKEN now lives
// ONLY server-side (FangateVault, resolved by the proxy via X-Fangate-Inbox);
// the browser keeps just a non-secret connection flag + the contact email so
// the UI renders synchronously without ever holding the token.
const connectedKey = id => `fangate_connected_${id}`;
const emailKey = id => `fangate_email_${id}`;
// Whether the Live-Sales webhook has a signing secret on file (drives the
// Chat-Accounts "Webhook" column). Cached like the connection flag.
const webhookSetKey = id => `fangate_webhook_set_${id}`;
const readWebhookSet = id => {
  try { return localStorage.getItem(webhookSetKey(id)) === '1'; } catch (_) { return false; }
};
const writeWebhookSet = (id, on) => {
  try {
    if (on) localStorage.setItem(webhookSetKey(id), '1');
    else    localStorage.removeItem(webhookSetKey(id));
  } catch (_) { /* storage unavailable — ignore */ }
};

// 3-state Live-Sales webhook health for the Chat-Accounts "Webhook" column:
// 'not_connected' | 'connected' | 'errors'. Server-computed (secret on file +
// last delivery outcome); cached so the list paints instantly between fetches.
const webhookStateKey = id => `fangate_webhook_state_${id}`;
const readWebhookState = id => {
  try { return localStorage.getItem(webhookStateKey(id)) || 'not_connected'; } catch (_) { return 'not_connected'; }
};
const writeWebhookState = (id, state) => {
  try {
    if (state && state !== 'not_connected') localStorage.setItem(webhookStateKey(id), state);
    else localStorage.removeItem(webhookStateKey(id));
  } catch (_) { /* storage unavailable — ignore */ }
};

// Active inbox id is set from the panel/layout when a conversation is opened
// or a chat-account is selected. Without it, no token is sent.
let activeInboxId = null;
const setActiveInbox = id => { activeInboxId = id || null; };
const getActiveInbox = () => activeInboxId;

const baseUrl = () =>
  (typeof window !== 'undefined' && window.FANGATE_DIRECT)
    ? FANGATE_DIRECT_URL
    : FANGATE_BASE_URL;

// localStorage is the SYNCHRONOUS client cache (templates/computeds read it
// without awaiting). It holds NO secret — just a connection flag + email; the
// server (FangateVault) is the durable source of truth, and the only home of
// the token.
const readConnected = id => {
  try { return localStorage.getItem(connectedKey(id)) === '1'; } catch (_) { return false; }
};
const writeConnected = (id, on) => {
  try {
    if (on) localStorage.setItem(connectedKey(id), '1');
    else    localStorage.removeItem(connectedKey(id));
  } catch (_) { /* storage unavailable (private mode / quota) — ignore */ }
};
const readLocalEmail = id => {
  try { return localStorage.getItem(emailKey(id)) || ''; } catch (_) { return ''; }
};
const writeLocalEmail = (id, e) => {
  try {
    if (e) localStorage.setItem(emailKey(id), e);
    else   localStorage.removeItem(emailKey(id));
  } catch (_) { /* storage unavailable — ignore */ }
};

// ---- Server-side vault store (#91b "Variante A") ---------------------------
// Same-origin, Chatwoot-authenticated endpoints (ACL = inbox's account):
//   GET    /integrations/fangate_token/:inboxId  -> { connected, email }   (NO token)
//   PUT    /integrations/fangate_token/:inboxId  { token, email } | { source_inbox_id, email }
//   DELETE /integrations/fangate_token/:inboxId  -> { connected: false }
// The token never comes back to the browser; proxy calls send X-Fangate-Inbox
// and Rails resolves + attaches the stored token.
const tokenEndpoint = id => `/integrations/fangate_token/${id}`;
const salesEndpoint = id => `/integrations/fangate_sales/${id}`;

// Live-Sales for an inbox — server-paginated + server-aggregated (the controller
// does the period/status/sort/search/buyer work in Postgres). `opts`:
//   { from, to (ISO), view: 'tx'|'buyers', status, search, sort, page, perPage }
// Returns the WHOLE payload { sales|buyers, summary, totals, meta } so the caller
// gets the period tiles + pagination meta alongside the current page.
const getSales = async (id, opts = {}) => {
  if (!id) return {};
  try {
    const params = {
      from: opts.from || undefined,
      to: opts.to || undefined,
      view: opts.view || undefined,
      status: opts.status || undefined,
      search: opts.search || undefined,
      sort: opts.sort || undefined,
      page: opts.page || undefined,
      per_page: opts.perPage || undefined,
      conversation_id: opts.conversationId || undefined, // view=this_chat
    };
    const resp = await axios.get(salesEndpoint(id), { params });
    return resp?.data || {};
  } catch (_) {
    return {};
  }
};

// Persistent buyer-email -> Telegram-contact link for the panel's "This Chat"
// tab. POSTs to our own Rails endpoint (NOT the Fangate proxy). Returns the
// created link payload, or throws (so the modal can surface the failure).
const assignChatLink = async (inboxId, { buyerEmail, conversationId } = {}) => {
  const resp = await axios.post('/integrations/fangate_chat_links', {
    inbox_id: inboxId,
    buyer_email: buyerEmail,
    conversation_id: conversationId,
  });
  return resp?.data || {};
};

// Agents-performance report (Reports → Agents performance): seller earning +
// sale count per agent across their assigned accounts, in a date window.
const getAgentsPerformance = async (accountId, from, to) => {
  if (!accountId) return [];
  try {
    const resp = await axios.get('/integrations/fangate_agents_performance', {
      params: { account_id: accountId, from: from || undefined, to: to || undefined },
    });
    return resp?.data?.agents || [];
  } catch (_) {
    return [];
  }
};

// Best-effort partial PUT of any subset of { token, email, source_inbox_id }.
const putToServer = (id, body) => {
  try {
    const p = axios.put(tokenEndpoint(id), body);
    if (p && p.catch) p.catch(() => {});
  } catch (_) { /* axios unavailable — server reconciles on next hydrate */ }
};

// The token is server-only now. Kept as a no-op (returns '') so any stray
// caller doesn't break; callers that need to act on "is it linked" use the
// connection flag below.
const getTokenForInbox = () => '';

const setTokenForInbox = (id, t) => {
  if (!id) return;
  writeConnected(id, !!t);          // immediate, synchronous — UI reflects it
  if (!t) writeLocalEmail(id, '');  // unlinking clears the cached email too
  try {
    const p = t ? axios.put(tokenEndpoint(id), { token: t }) : axios.delete(tokenEndpoint(id));
    if (p && p.catch) p.catch(() => {});
  } catch (_) { /* server holds the durable copy; next hydrate reconciles */ }
};

// Per-inbox vault email (read from the sync cache).
const getEmailForInbox = id => (id ? readLocalEmail(id) : '');

// Set just the email (editing it later without touching the token).
const setEmailForInbox = (id, e) => {
  if (!id) return;
  writeLocalEmail(id, e);
  putToServer(id, { email: e });
};

// Link a vault in one shot: token + email together. Single server request, so
// the two columns never race. Used by the Add-Account wizard on connect.
const setLinkForInbox = (id, { token, email } = {}) => {
  if (!id) return;
  writeConnected(id, !!token);
  writeLocalEmail(id, email);
  putToServer(id, { token, email });
};

// Link this inbox to the SAME vault as another inbox — the server copies the
// stored token across; the raw token never touches the browser.
const linkExistingForInbox = (id, sourceInboxId, email) => {
  if (!id || !sourceInboxId) return;
  writeConnected(id, true);
  if (email != null) writeLocalEmail(id, email);
  putToServer(id, { source_inbox_id: sourceInboxId, email });
};

// ---- Live-Sales webhook (per inbox) ----------------------------------------
// The receiving URL (https://<host>/webhooks/fangate/<token>) you paste into
// Fangate's "Register webhook" dialog. Admin-only server-side; returns '' for
// non-admins or an unconnected vault. webhookSecretSet reflects whether the
// (optional) Fangate signing secret is on file.
const getWebhookInfo = async id => {
  if (!id) return { webhookUrl: '', webhookSecretSet: false };
  try {
    const resp = await axios.get(tokenEndpoint(id));
    return {
      webhookUrl: resp?.data?.webhook_url || '',
      webhookSecretSet: !!resp?.data?.webhook_secret_set,
    };
  } catch (_) {
    return { webhookUrl: '', webhookSecretSet: false };
  }
};
// Store (or clear, by passing '') the Fangate-issued signing secret. Admin-only.
const setWebhookSecret = (id, secret) => {
  if (!id) return Promise.resolve();
  return axios.put(tokenEndpoint(id), { webhook_secret: secret || '' });
};

// Pull connection state down from the server into the sync cache. Returns the
// connected boolean. On error, trusts whatever's already cached.
const hydrateInbox = async id => {
  if (!id) return false;
  try {
    const resp = await axios.get(tokenEndpoint(id));
    const connected = !!resp?.data?.connected;
    writeConnected(id, connected);
    writeLocalEmail(id, resp?.data?.email || '');
    writeWebhookSet(id, !!resp?.data?.webhook_secret_set);
    writeWebhookState(id, resp?.data?.webhook_state || 'not_connected');
    return connected;
  } catch (_) {
    return readConnected(id);
  }
};

const hydrateInboxes = ids =>
  Promise.all((ids || []).filter(Boolean).map(hydrateInbox));

// Normal vault calls send the ACTIVE inbox id; Rails resolves the stored token
// server-side (X-Fangate-Inbox) and attaches the real Authorization: Bearer
// header upstream. The raw token never travels through the browser.
const headers = (extra = {}) => {
  const base = { Accept: 'application/json', ...extra };
  if (!activeInboxId) return base;
  return { ...base, 'X-Fangate-Inbox': String(activeInboxId) };
};

// Force the server-side proxy cache (Redis) to bypass its entry and refetch
// from Fangate. Sent by the "Reload" / refresh buttons (refresh: true) so a
// manual refresh is always live, while normal navigation rides the shared
// cache. No-op header when refresh is falsy.
const refreshHeader = refresh => (refresh ? { 'X-Fangate-Refresh': '1' } : {});

// Translate axios failure into something a human can read.
// Particular focus: CORS (axios reports as generic "Network Error" with no
// response object) vs HTTP errors (response present with status).
//
// On HTTP errors, we surface the upstream's own field (`message` / `error` /
// `error_description`) so the reason is visible (e.g. "Unauthenticated.",
// "Your email address is not verified.", "Invalid ability") — critical for
// telling "invalid token" apart from "right token, wrong scope/state".
//
// We also dump the raw response to console.warn so unexpected shapes surface
// during debugging without needing to crack open DevTools' network tab.
const friendlyError = err => {
  if (!err) return 'Unknown error';
  if (err.response) {
    const s    = err.response.status;
    const data = err.response.data;
    // Axios will auto-parse JSON when Content-Type is JSON. If the proxy
    // forwarded the body as a string, parse it ourselves.
    const parsed = (typeof data === 'string' && data.trim().startsWith('{'))
      ? (() => { try { return JSON.parse(data); } catch (_) { return null; } })()
      : data;
    // Fangate uses `errors_message`; other Laravel/Sanctum apps may use any
    // of `message` / `error_description` / `error` / `detail`. Surface
    // whichever is set so the user sees Fangate's actual reason.
    const detail =
      parsed?.errors_message ||
      parsed?.message ||
      parsed?.error_description ||
      parsed?.error   ||
      parsed?.detail  ||
      (typeof parsed === 'string' && parsed.trim() ? parsed.slice(0, 200) : '');

    // Dump raw response + URL so we can diagnose unexpected shapes AND
    // know WHICH endpoint failed. Visible in DevTools console.
    const url    = err.config?.url || err.response?.config?.url || '?';
    const method = (err.config?.method || err.response?.config?.method || 'GET').toUpperCase();
    // eslint-disable-next-line no-console
    console.warn(`[Fangate] HTTP ${s} ${method} ${url} body:`, data);

    if (s === 401 || s === 403) {
      return detail
        ? `Fangate rejected the token (HTTP ${s}): ${detail}`
        : `Fangate rejected the token (HTTP ${s}). Empty response body — check DevTools → Network → /integrations/fangate_proxy/user for the actual Fangate response.`;
    }
    if (s === 404) {
      // Two sources for 404 here: our own proxy rejecting an un-whitelisted
      // path, or Fangate's API saying it doesn't recognize the path.
      // Our proxy body is `{ error: "Endpoint not allowed: <method> <path>" }`,
      // so surface that distinction so debugging isn't guesswork.
      if (typeof detail === 'string' && detail.startsWith('Endpoint not allowed')) {
        return `Proxy blocked: ${detail}. Whitelist needs an entry — Rails may also need a restart.`;
      }
      return detail
        ? `Fangate returned HTTP 404: ${detail}. API path may have changed.`
        : 'Fangate endpoint not found (HTTP 404). API path may have changed.';
    }
    if (s >= 500)  return `Fangate server error (HTTP ${s})${detail ? ': ' + detail : ''}. Try again in a minute.`;
    return detail ? `Fangate returned HTTP ${s}: ${detail}` : `Fangate returned HTTP ${s}.`;
  }
  // No response → likely CORS or network outage. Axios uses the literal
  // "Network Error" string in this case.
  if ((err.message || '').toLowerCase().includes('network')) {
    return 'CORS-blocked or network down. Fangate didn\'t allow the browser to call its API directly. See setup notes — needs Rails proxy in dev or a browser CORS extension for quick testing.';
  }
  return err.message || String(err);
};

// ---- Endpoints --------------------------------------------------------------
const FangateAPI = {
  // ----- Connection state (scoped to active inbox) -----
  isConnected: () => readConnected(activeInboxId),

  // ----- Per-inbox scope (used by Chat-Account settings) -----
  setActiveInbox,
  getActiveInbox,
  getTokenForInbox,
  setTokenForInbox,
  getEmailForInbox,
  setEmailForInbox,
  setLinkForInbox,
  linkExistingForInbox,
  hydrateInbox,
  getSales,
  assignChatLink,
  getAgentsPerformance,
  hydrateInboxes,
  isInboxConnected: id => readConnected(id),
  getWebhookInfo,
  setWebhookSecret,
  isWebhookSet: id => readWebhookSet(id),
  getWebhookState: id => readWebhookState(id),
  setWebhookStateCache: (id, state) => writeWebhookState(id, state),
  setWebhookSetCache: (id, on) => writeWebhookSet(id, on),

  // Verifies a token by listing one product (a generated API key has access
  // to /products but NOT to /user — Fangate returns 403 with
  // "Account settings are not available for generated API keys." on /user).
  // Returns a small "ok" payload on success, throws on failure.
  async validateToken(token) {
    const direct = typeof window !== 'undefined' && window.FANGATE_DIRECT;
    const reqHeaders = direct
      ? { Accept: 'application/json', Authorization: `Bearer ${token}` }
      : { Accept: 'application/json', 'X-Fangate-Token': token };
    try {
      await axios.get(`${baseUrl()}/products`,
        { headers: reqHeaders, params: { page: 1, limit: 1 } });
      return { ok: true };
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // Validate an already-STORED vault by pinging the proxy with X-Fangate-Inbox
  // (Rails attaches the server-side token). Lets the edit/refresh flow check a
  // link's health without the client ever holding the token.
  async validateInbox(inboxId) {
    try {
      await axios.get(`${baseUrl()}/products`,
        { headers: { Accept: 'application/json', 'X-Fangate-Inbox': String(inboxId) }, params: { page: 1, limit: 1 } });
      return { ok: true };
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ----- Creator profile -----
  // GET /api/user → { id, email, name, bank_data, is_verified, … }
  async getUser() {
    try {
      const resp = await axios.get(`${baseUrl()}/user`, { headers: headers() });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ----- Products / content -----
  // GET /api/products?page=1&limit=20&sort_by=&folder=
  async listProducts({ page = 1, limit = 20, sortBy, folder, refresh = false, inboxId } = {}) {
    try {
      const params = { page, limit };
      if (sortBy) params.sort_by = sortBy;
      if (folder) params.folder  = folder;
      // Optional per-call inbox override (X-Fangate-Inbox) so a component can
      // read a specific vault's products without changing the global active
      // inbox — used by the chat's "shared links" panel.
      const h = { ...headers(refreshHeader(refresh)) };
      if (inboxId) h['X-Fangate-Inbox'] = String(inboxId);
      const resp = await axios.get(`${baseUrl()}/products`, { headers: h, params });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ----- Media library (bundle building blocks) -----
  // GET /api/media?page&limit&status&folder_id&unlinked — the creator's media
  // pool. Each item: { id, type, title, status(pending|approved|rejected),
  // preview, preview_blurred, source, folder_id, product_ids[], is_linked }.
  // Returns the FULL body (data + pagination meta) so callers can page.
  async listMedia({ page = 1, limit = 50, unlinked, folderId, status, refresh = false } = {}) {
    try {
      const params = { page, limit };
      if (unlinked) params.unlinked = true;
      if (folderId) params.folder_id = folderId;
      if (status)   params.status = status;
      const resp = await axios.get(`${baseUrl()}/media`,
        { headers: headers(refreshHeader(refresh)), params });
      return resp?.data ?? {};
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // POST /api/products with media_ids[] → create a BUNDLE (a product that
  // references several existing media rows). Fangate minimum price = 500 minor
  // units (€5.00). is_verif_age (Yoti age-check of the buyer) is an opt-in flag
  // (default off); is_adult_content is kept fixed-true (sensible creator-content
  // default, no UI toggle); downloadable + consent are user-togglable.
  async createBundle({
    mediaIds, price, title, publicDescription,
    isDownloadable = true, isVerifyAge = false, isShouldConsent = true,
  } = {}) {
    if (!Array.isArray(mediaIds) || !mediaIds.length) throw new Error('createBundle: mediaIds required.');
    const body = {
      media_ids: mediaIds,
      price,
      is_verif_age: !!isVerifyAge,
      is_downloadable: !!isDownloadable,
      is_adult_content: true,
      is_should_consent: !!isShouldConsent,
    };
    if (title)             body.title              = title;
    if (publicDescription) body.public_description = publicDescription;
    try {
      const resp = await axios.post(`${baseUrl()}/products`, body,
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  async getProduct(id, { refresh = false, inboxId } = {}) {
    try {
      const h = headers(refreshHeader(refresh));
      if (inboxId) h['X-Fangate-Inbox'] = String(inboxId); // per-call vault override (Reports view)
      const resp = await axios.get(`${baseUrl()}/products/${id}`, { headers: h });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ===========================================================================
  // CO-CREATOR CONSENT TAGS
  //
  // Earlier docs research said this whole group was dashboard-session-only.
  // Fangate's own support has since said API keys CAN use it; reactivated
  // here so we can empirically verify and wire UI to real calls.
  // If endpoints still 403, the friendlyError() will surface that and
  // we'll either back out or escalate to Fangate.
  // ===========================================================================

  // GET /api/creators/search?q=&limit=&exclude_user_id=
  // Typeahead for creators to tag. Returns [{ id, first_name, last_name,
  // email, display_name }, ...].
  async searchCreators(query, { limit, excludeUserId } = {}) {
    const params = {};
    if (query)          params.q                = query;
    if (limit)          params.limit            = limit;
    if (excludeUserId)  params.exclude_user_id  = excludeUserId;
    try {
      const resp = await axios.get(`${baseUrl()}/creators/search`, { headers: headers(), params });
      return resp?.data?.data ?? resp?.data ?? [];
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // GET /api/products/{productId}/consent-creator-tags
  // Owner reads current consent tags on their product. Returns
  // [{ id, status: "pending"|"accepted", product_id, taken_at, created_at,
  //    tagged_user: { id, first_name, last_name, email, display_name } }, ...]
  async listProductConsentTags(productId, { refresh = false } = {}) {
    try {
      const resp = await axios.get(`${baseUrl()}/products/${productId}/consent-creator-tags`,
        { headers: headers(refreshHeader(refresh)) });
      return resp?.data?.data ?? resp?.data ?? [];
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // POST /api/consents/store-or-send (multipart)
  // One call creates a consent on a product in EXACTLY ONE mode:
  //   • Creator-tag mode:  taggedCreatorId  OR taggedCreatorIds=[]
  //   • Email-only mode:   email
  //   • Documents mode:    files=[] (>=3 PDF/JPG/PNG, <=10MB each)
  // `date` is the consent date string (required by Fangate).
  // Sending the request happens in this same call — there is no
  // separate "draft" then "send" step.
  // Backend has two implicit conversions:
  //   • email-only with an address matching an existing Fangate user →
  //     auto-rewritten as creator-tag flow.
  //   • If seller+tagged user already have an `accepted` tag on another
  //     product, the new tag auto-creates as `accepted`.
  async addProductConsentTag({ productId, date, taggedCreatorId, taggedCreatorIds, email, files } = {}) {
    if (!productId) throw new Error('addProductConsentTag: productId is required.');
    if (!date)      throw new Error('addProductConsentTag: date is required.');
    const fd = new FormData();
    fd.append('product_id', String(productId));
    fd.append('date', String(date));
    if (taggedCreatorId != null) {
      fd.append('tagged_creator_id', String(taggedCreatorId));
    } else if (Array.isArray(taggedCreatorIds) && taggedCreatorIds.length) {
      taggedCreatorIds.forEach(id => fd.append('tagged_creator_ids[]', String(id)));
    } else if (email) {
      fd.append('email', email);
    } else if (Array.isArray(files) && files.length) {
      files.forEach(f => fd.append('files[]', f));
    } else {
      throw new Error('addProductConsentTag: pick exactly one mode (creator id(s), email, or files).');
    }
    try {
      const resp = await axios.post(`${baseUrl()}/consents/store-or-send`, fd,
        { headers: { ...headers() } });  // Content-Type left to axios for multipart boundary
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // GET /api/consent-tags — the TAGGED creator (you) lists incoming requests.
  // Useful when you want to see "who has tagged me on what?".
  // `inboxId` overrides the X-Fangate-Inbox header for THIS call only (without
  // touching the global active inbox) so the notification poller can check a
  // specific vault while the user is looking at some other chat-account.
  async listIncomingConsentTags({ inboxId } = {}) {
    const h = { Accept: 'application/json' };
    const id = inboxId || activeInboxId;
    if (id) h['X-Fangate-Inbox'] = String(id);
    try {
      const resp = await axios.get(`${baseUrl()}/consent-tags`, { headers: h });
      return resp?.data?.data ?? resp?.data ?? [];
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // PATCH /api/consent-tags/{id} — the tagged creator accepts or declines.
  // decision: 'accept' | 'decline'.
  // Note: if the tagged creator is not Veriff-verified, Fangate returns a
  // 4xx with `requires_verification: true` and a Veriff URL the user must
  // complete before re-issuing this call.
  async respondToConsentTag(tagId, decision, { inboxId } = {}) {
    const h = { ...headers(), 'Content-Type': 'application/json' };
    if (inboxId) h['X-Fangate-Inbox'] = String(inboxId);   // per-call vault override
    try {
      const resp = await axios.patch(`${baseUrl()}/consent-tags/${tagId}`,
        { decision }, { headers: h });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // PATCH /api/products/{id} → update a product's editable METADATA only.
  // Payload shape (all fields optional, send only what changed):
  //   title, public_description, internal_notes, folder_id,
  //   is_adult_content, is_verif_age, is_downloadable, in_collection,
  //   is_should_consent
  // NOTE: `price` is NOT editable through this endpoint — use
  // updateProductPrice() instead (Fangate has a dedicated /price endpoint).
  // Requires the `can_create_new_urls` ability.
  async updateProduct(id, payload) {
    try {
      const resp = await axios.patch(`${baseUrl()}/products/${id}`, payload,
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // PATCH /api/products/{id}/price → change the price of an EXISTING product link.
  //   • Mutates the active /AbCdEf short link in place — the URL slug stays the same.
  //   • In-flight checkouts keep their locked original price.
  //   • Argument is integer cents (e.g. 1500 = €15.00). Caller converts.
  // Returns the updated { id, link, price } record from Fangate.
  // Requires the `can_create_new_urls` (urls.write) ability.
  async updateProductPrice(id, priceCents) {
    try {
      const resp = await axios.patch(`${baseUrl()}/products/${id}/price`,
        { price: priceCents },
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // POST /api/products/{sourceId}/price-links → DUPLICATE the product into a
  // new product record with its own short link + own price.
  // VERY IMPORTANT: this DOES NOT add a "variant" to the source — it creates
  // a fully independent new product. The returned `id` is a NEW product id
  // and will appear separately in the next GET /products listing. Caller is
  // responsible for tracking the source→clone relationship (we use
  // media-overlap + localStorage, see FangatePanel).
  // Payload:
  //   price (integer cents, required)
  //   title (string, optional — defaults to source's title)
  //   public_description (string, optional)
  //   private_description (string, optional)
  // Returns the new { id, title, link, price } from Fangate.
  // Requires the `can_create_new_urls` ability.
  async createPriceLink(sourceId, { price, title, publicDescription, privateDescription } = {}) {
    const payload = { price };
    if (title != null)              payload.title               = title;
    if (publicDescription != null)  payload.public_description  = publicDescription;
    if (privateDescription != null) payload.private_description = privateDescription;
    try {
      const resp = await axios.post(`${baseUrl()}/products/${sourceId}/price-links`,
        payload,
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ===========================================================================
  // UPLOAD-SESSIONS flow — chunked multipart upload pipeline.
  //
  // Sequence (one file):
  //   1. createUploadSession(meta)                  → session id + strategy
  //   2. (multipart only) for each chunk:
  //        getUploadPart(sessionId, partNum)        → presigned S3 PUT URL
  //        putChunkToS3(presigned, chunkBlob)       → ETag from S3
  //   3. completeUploadSession(sessionId, parts)    → kicks off finalization
  //   4. waitForUploadReady(sessionId)              → polls until status=ready
  //   5. createProduct({ uploadSessionId, ... })    → returns the new product
  //
  // Multi-file products (e.g. a photo set): pass the first session's
  // resulting product.id as `productId` on the next createUploadSession;
  // Fangate attaches the new media to the existing product.
  // ===========================================================================

  // Fangate's documented thresholds and limits (from /api-reference/upload-sessions).
  UPLOAD_LIMITS: {
    MAX_FILE_BYTES:      5 * 1024 * 1024 * 1024,  // 5 GB hard ceiling
    MULTIPART_THRESHOLD: 100 * 1024 * 1024,       // ≤ 100 MB → single_part
    MIN_PART_BYTES:      8 * 1024 * 1024,         // 8 MB except for the last part
    PRESIGN_TTL_SEC:     15 * 60,                 // 15 min — re-request if expired
    ALLOWED_IMAGE_MIMES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'],
    ALLOWED_VIDEO_MIMES: ['video/mp4',  'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm'],
  },

  // POST /api/upload-sessions → init a new session for a single file.
  //   session_type = 'device_multipart' (we don't use 'remote_import')
  //   asset_kind, file_name, file_size_bytes, mime_type are required for device_multipart.
  //   product_id (optional) — when adding media to an existing product.
  // Returns the session: { id, upload_strategy, initial_upload, … }.
  async createUploadSession({ fileName, fileSize, mimeType, assetKind, productId } = {}) {
    const body = {
      session_type:    'device_multipart',
      asset_kind:      assetKind,
      file_name:       fileName,
      file_size_bytes: fileSize,
      mime_type:       mimeType,
    };
    if (productId != null) body.product_id = productId;
    try {
      const resp = await axios.post(`${baseUrl()}/upload-sessions`, body,
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // POST /api/upload-sessions/{id}/parts → request a presigned S3 PUT URL for
  // one part of the multipart upload. The actual chunk bytes are NOT sent
  // through this endpoint — they go directly browser → S3 via the returned
  // `url`, with the returned `headers` map attached.
  async getUploadPart(sessionId, partNumber) {
    try {
      const resp = await axios.post(`${baseUrl()}/upload-sessions/${sessionId}/parts`,
        { part_number: partNumber },
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      const data = resp?.data?.data ?? resp?.data;
      return data?.part || data;   // { part_number, method, url, headers, expires_at }
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // PUT raw chunk bytes to the presigned S3 URL Fangate handed back.
  // Bypasses our Rails proxy entirely (browser → S3 direct). Returns the
  // S3 ETag that /complete needs.
  async putChunkToS3(part, chunkBlob) {
    if (!part?.url) throw new Error('Internal: presigned part is missing url');
    try {
      const resp = await axios.put(part.url, chunkBlob, {
        headers: { ...(part.headers || {}) },
        // Cross-origin to S3 — no withCredentials, no Chatwoot auth.
        // Send raw bytes (axios won't transform a Blob).
        transformRequest: [x => x],
      });
      // S3 returns the ETag in lowercase or canonical header — be defensive.
      const etag = resp?.headers?.etag || resp?.headers?.ETag || resp?.headers?.['ETag'];
      if (!etag) throw new Error('S3 returned no ETag — cannot complete the part');
      return String(etag).replace(/^"|"$/g, '');   // strip the wrapping quotes
    } catch (e) {
      throw new Error('Chunk upload failed (S3 PUT): ' + (e?.message || String(e)));
    }
  },

  // POST /api/upload-sessions/{id}/complete → finalize the session.
  //   parts: [{ part_number, etag, size_bytes }, ...]  (multipart only; omit for single_part / remote_import)
  // 200 = ready; 202 = async finalization continuing — caller should poll.
  async completeUploadSession(sessionId, parts = null) {
    const body = Array.isArray(parts) && parts.length ? { parts } : {};
    try {
      const resp = await axios.post(`${baseUrl()}/upload-sessions/${sessionId}/complete`,
        body,
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // GET /api/upload-sessions/{id} → status check.
  // Lifecycle: created → uploading → uploaded → finalizing → ready (terminal: failed/aborted/expired).
  async getUploadSession(sessionId) {
    try {
      const resp = await axios.get(`${baseUrl()}/upload-sessions/${sessionId}`,
        { headers: headers() });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // Poll getUploadSession until the session reaches a terminal state.
  // Exponential backoff capped at 10s; total timeout default 5 min.
  // Returns the final session object on `ready`. Throws on failed/aborted/expired.
  async waitForUploadReady(sessionId, { maxSeconds = 300, onTick } = {}) {
    const deadline = Date.now() + (maxSeconds * 1000);
    let delay = 1000;
    while (Date.now() < deadline) {
      const session = await this.getUploadSession(sessionId);
      const status = session?.status;
      if (onTick) onTick(status, session);
      if (status === 'ready') return session;
      if (['failed', 'aborted', 'expired'].includes(status)) {
        throw new Error(`Upload session ${status}` + (session?.errors_message ? `: ${session.errors_message}` : ''));
      }
      await new Promise(r => setTimeout(r, delay));
      delay = Math.min(delay * 2, 10000);
    }
    throw new Error('Upload session did not reach "ready" within ' + maxSeconds + 's.');
  },

  // DELETE /api/upload-sessions/{id} → abort an in-progress upload (user
  // cancel or fatal mid-upload error). Best-effort — errors are swallowed.
  async abortUploadSession(sessionId) {
    try {
      await axios.delete(`${baseUrl()}/upload-sessions/${sessionId}`,
        { headers: headers() });
    } catch (_) { /* best effort */ }
  },

  // POST /api/products with upload_session_id → CREATE the product from a
  // ready session.
  // Accepts (per docs): title, price (cents), public_description,
  // private_description, is_adult_content, is_verif_age, is_should_consent.
  // NOTE: is_downloadable / folder_id / in_collection are NOT accepted here.
  // Set those via subsequent PATCH /products/{id}, PATCH /folder, POST /collection.
  async createProduct({
    uploadSessionId, price, title,
    publicDescription, privateDescription,
    isAdultContent, isVerifAge, isShouldConsent,
  } = {}) {
    const body = { upload_session_id: uploadSessionId, price };
    if (title              != null) body.title               = title;
    if (publicDescription  != null) body.public_description  = publicDescription;
    if (privateDescription != null) body.private_description = privateDescription;
    if (isAdultContent     != null) body.is_adult_content    = !!isAdultContent;
    if (isVerifAge         != null) body.is_verif_age        = !!isVerifAge;
    if (isShouldConsent    != null) body.is_should_consent   = !!isShouldConsent;
    try {
      const resp = await axios.post(`${baseUrl()}/products`, body,
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ----- Content folders (creator-owned folder taxonomy) -----
  // GET /api/content-folders → [{ id, name, products_count, created_at, … }, …]
  // Folders live at the CREATOR level (not per-product) and let the creator
  // group products. A product is in a folder via products.folder_id.
  async listContentFolders({ refresh = false } = {}) {
    try {
      const resp = await axios.get(`${baseUrl()}/content-folders`,
        { headers: headers(refreshHeader(refresh)) });
      const data = resp?.data?.data ?? resp?.data;
      return Array.isArray(data) ? data : (data?.data || []);
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // POST /api/content-folders → create a new folder. Body: { name }.
  // Returns the created folder { id, name, … }.
  async createContentFolder(name) {
    try {
      const resp = await axios.post(`${baseUrl()}/content-folders`,
        { name },
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // PATCH /api/products/{id}/folder → set or clear folder membership.
  // Body: { folder_id: number|null }. Null removes from folder.
  // SEPARATE from PATCH /products/{id} — the products endpoint does NOT
  // accept a folder field (Fangate ignores it silently).
  async updateProductFolder(productId, folderId) {
    try {
      const resp = await axios.patch(`${baseUrl()}/products/${productId}/folder`,
        { folder_id: folderId },
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // POST /api/products/{id}/collection → toggle in/out of the collection.
  // Calling once adds, calling again removes (per docs).
  async toggleProductCollection(productId) {
    try {
      const resp = await axios.post(`${baseUrl()}/products/${productId}/collection`,
        {},
        { headers: { ...headers(), 'Content-Type': 'application/json' } });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // GET /api/products/collection → products currently on the creator's
  // public collection page. Same per-item schema as listProducts. Requires
  // the `collection-urls.read` ability (always-on for generated API keys
  // per the docs).
  async getProductsCollection({ refresh = false } = {}) {
    try {
      const resp = await axios.get(`${baseUrl()}/products/collection`,
        { headers: headers(refreshHeader(refresh)) });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ----- Wallet / sales -----
  // GET /api/wallet → { available, hold, pending, total, transactions: [...] }
  async getWallet({ refresh = false } = {}) {
    try {
      const resp = await axios.get(`${baseUrl()}/wallet`,
        { headers: headers(refreshHeader(refresh)) });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // GET /api/dashboard/summary → aggregated KPIs (sales/conv/top-products)
  async getDashboardSummary({ refresh = false } = {}) {
    try {
      const resp = await axios.get(`${baseUrl()}/dashboard/summary`,
        { headers: headers(refreshHeader(refresh)) });
      return resp?.data?.data ?? resp?.data;
    } catch (e) { throw new Error(friendlyError(e)); }
  },

  // ----- Local helpers -----
  buildProductLink(productId) {
    // Public sales link pattern — the API also returns a `link` field on
    // each product, but this fallback works for any id.
    return `https://fangate.info/l/${productId}`;
  },
};

export default FangateAPI;
